import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Baby,
  MapPin,
  Phone,
  Mail,
  Clock,
  Send,
  Building2,
  MessageSquare
} from 'lucide-react';

export const ContactPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    toast.success('Mesajınız başarıyla gönderildi! En kısa sürede size dönüş yapacağız.');
    setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    setSending(false);
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Adres',
      content: 'Emekevler Mah. D-100 Karayolu Cad.\nNeu Plaza No: 84/3\nKartepe / KOCAELİ',
      color: 'bg-blue-100 text-blue-600'
    },
    {
      icon: Phone,
      title: 'Telefon',
      content: '+90 (262) 000 00 00',
      color: 'bg-green-100 text-green-600'
    },
    {
      icon: Mail,
      title: 'E-posta',
      content: 'info@neobreedai.com',
      color: 'bg-purple-100 text-purple-600'
    },
    {
      icon: Clock,
      title: 'Çalışma Saatleri',
      content: 'Pazartesi - Cumartesi\n09:00 - 18:00',
      color: 'bg-orange-100 text-orange-600'
    }
  ];

  // Google Maps embed URL for Kartepe, Kocaeli
  const mapEmbedUrl = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3019.8764567890123!2d29.9456789!3d40.6789012!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDDCsDQwJzQ0LjAiTiAyOcKwNTYnNDQuNCJF!5e0!3m2!1str!2str!4v1234567890";

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#5A67D8] to-[#4338CA] flex items-center justify-center">
                <Baby className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                NeoBreed AI
              </span>
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a href="/" className="text-slate-600 hover:text-slate-900 transition-colors">Ana Sayfa</a>
              <a href="/about" className="text-slate-600 hover:text-slate-900 transition-colors">Hakkımızda</a>
              <a href="/contact" className="text-[#5A67D8] font-medium">İletişim</a>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                onClick={() => navigate('/login')}
                className="text-slate-600"
              >
                Giriş Yap
              </Button>
              <Button
                onClick={() => navigate('/register')}
                className="bg-[#5A67D8] hover:bg-[#4338CA] text-white"
              >
                Ücretsiz Başla
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-[#F8F7FC] to-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#E0D7FF] rounded-full mb-6">
            <MessageSquare className="w-4 h-4 text-[#5A67D8]" />
            <span className="text-sm font-medium text-[#5A67D8]">7/24 Destek</span>
          </div>
          <h1 
            className="text-4xl sm:text-5xl font-bold text-slate-900 leading-tight mb-6"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            İletişim
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Sorularınız, önerileriniz veya işbirliği talepleriniz için bizimle iletişime geçin. 
            Size en kısa sürede dönüş yapacağız.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => (
              <Card key={index} className="border-2 border-slate-100 hover:border-[#5A67D8]/30 transition-colors">
                <CardContent className="p-6 text-center">
                  <div className={`w-12 h-12 rounded-xl ${info.color} flex items-center justify-center mx-auto mb-4`}>
                    <info.icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{info.title}</h3>
                  <p className="text-slate-600 text-sm whitespace-pre-line">{info.content}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Map & Form Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Map */}
            <Card className="border-2 border-slate-100 shadow-lg overflow-hidden">
              <CardContent className="p-0">
                <div className="aspect-square lg:aspect-auto lg:h-full min-h-[400px] bg-slate-200 relative">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d12099.856671609947!2d29.94!3d40.68!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14cb4c5b5c5b5c5b%3A0x5c5b5c5b5c5b5c5b!2sKartepe%2C%20Kocaeli!5e0!3m2!1str!2str!4v1620000000000!5m2!1str!2str"
                    width="100%"
                    height="100%"
                    style={{ border: 0, position: 'absolute', top: 0, left: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="NeoBreed AI Konum"
                  />
                </div>
                <div className="p-6 bg-white">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#E0D7FF] flex items-center justify-center flex-shrink-0">
                      <Building2 className="w-5 h-5 text-[#5A67D8]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">Konsültan Özel Sağlık Hizmetleri</h4>
                      <p className="text-slate-600 text-sm mt-1">
                        Emekevler Mah. D-100 Karayolu Cad.<br />
                        Neu Plaza No: 84/3, Kartepe/KOCAELİ
                      </p>
                      <a 
                        href="https://www.google.com/maps/search/?api=1&query=Emekevler+Mah+D-100+Karayolu+Cad+Neu+Plaza+84/3+Kartepe+Kocaeli"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#5A67D8] text-sm font-medium hover:underline mt-2 inline-block"
                      >
                        Google Maps'te Aç →
                      </a>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Contact Form */}
            <Card className="border-2 border-slate-100 shadow-lg">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Bize Yazın
                </h3>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Ad Soyad</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Adınız Soyadınız"
                        required
                        className="border-slate-200 focus:border-[#5A67D8]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">E-posta</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="email@example.com"
                        required
                        className="border-slate-200 focus:border-[#5A67D8]"
                      />
                    </div>
                  </div>
                  
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Telefon</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="+90 5XX XXX XX XX"
                        className="border-slate-200 focus:border-[#5A67D8]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="subject">Konu</Label>
                      <Input
                        id="subject"
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                        placeholder="Mesajınızın konusu"
                        required
                        className="border-slate-200 focus:border-[#5A67D8]"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="message">Mesajınız</Label>
                    <textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Mesajınızı buraya yazın..."
                      required
                      rows={5}
                      className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:border-[#5A67D8] focus:ring-1 focus:ring-[#5A67D8] resize-none"
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full bg-[#5A67D8] hover:bg-[#4338CA] text-white"
                    disabled={sending}
                  >
                    {sending ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                        Gönderiliyor...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Mesaj Gönder
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Company Info */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Card className="border-2 border-slate-100 bg-gradient-to-r from-slate-50 to-white">
            <CardContent className="p-8">
              <div className="text-center space-y-4">
                <h3 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  KONSÜLTAN ÖZEL SAĞLIK HİZMETLERİ GIDA HAYV. TURZ. SAN. VE TİC. LTD. ŞTİ.
                </h3>
                <div className="flex flex-wrap justify-center gap-6 text-sm text-slate-600">
                  <span><strong>Vergi Dairesi:</strong> Alemdar V.D.</span>
                  <span><strong>Vergi No:</strong> 576 051 2362</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#5A67D8] to-[#4338CA] flex items-center justify-center">
              <Baby className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold">NeoBreed AI</span>
          </div>
          <p className="text-slate-400 text-sm text-center">
            © 2026 Konsültan Özel Sağlık Hizmetleri Ltd. Şti. Tüm hakları saklıdır.
          </p>
          <div className="flex gap-6">
            <a href="/about" className="text-slate-400 hover:text-white text-sm transition-colors">Hakkımızda</a>
            <a href="/contact" className="text-slate-400 hover:text-white text-sm transition-colors">İletişim</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ContactPage;
